import { observable, action, computed } from 'mobx';

import GroupShowService from 'services/GroupShowService';
import FirebaseRoomService from 'services/FirebaseRoomService';
import FirebaseService from 'services/FirebaseService';
import {
  BUY_GROUP_SHOW_TICKET_RESPONSE,
  CONNECT_TO_GROUP_SHOW_CHAT,
  GET_PLAY_TOKEN_FOR_GROUP_SHOW,
  REMAINING_TIME_UNTIL_GROUP_SHOW,
  TICKET_SELLING_FAILED,
  TICKET_SELLING_STARTED,
  UPDATE_GS_SUCCESS,
} from 'services/MessagingService/EventNames';
import messagingService from 'services/MessagingService/MessagingService';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import APIStates from 'enum/APIStates';
import { HOUR_AS_SECONDS } from 'enum/timeFormat';
import { NEW_SHOWS_STATE } from 'enum/videoPlayer';
import {
  getTimeStringWithHours,
  getTimeStringWithoutHours,
} from 'utils/helpers';


class GroupShowStore {
  @observable buyGroupShowTicketResponse = {};
  @observable remainingTimeLeftUntillGroupShow = null;
  @observable groupShowTransition = true;
  @observable groupShowTimer = 0;
  @observable isBonusTimeVisible = false;
  @observable showDescription = '';
  @observable ticketCost = 10;
  @observable ticketGoal = 2;
  @observable showLength = 5;
  @observable timeLimit = 5;
  @observable bonusTime = 5;
  @observable showErrorMsg = false;
  @observable ticketSellingStatus = APIStates.INITIAL;
  @observable updateGroupShowStatus = APIStates.INITIAL;

  constructor(messagingService) {
    this.messagingService = messagingService();
    this.messagingService.on(BUY_GROUP_SHOW_TICKET_RESPONSE, this.handleBuyGroupShowTicketResposne);
    this.messagingService.on(REMAINING_TIME_UNTIL_GROUP_SHOW, this.handleRemainingTimeUntillGroupShow);
    this.messagingService.on(CONNECT_TO_GROUP_SHOW_CHAT, this.handleConnectToGroupShowChat);
    this.messagingService.on(GET_PLAY_TOKEN_FOR_GROUP_SHOW, this.getPlayTokenForGroupShow);
    this.messagingService.on(TICKET_SELLING_STARTED, this.handleTicketSellingStarted);
    this.messagingService.on(UPDATE_GS_SUCCESS, this.handleGsUpdateSuccess);
    this.messagingService.on(TICKET_SELLING_FAILED, this.handleTicketSellingFail);
  }

  destructor() {
    this.messagingService.off(BUY_GROUP_SHOW_TICKET_RESPONSE, this.handleBuyGroupShowTicketResposne);
    this.messagingService.off(REMAINING_TIME_UNTIL_GROUP_SHOW, this.handleRemainingTimeUntillGroupShow);
    this.messagingService.off(CONNECT_TO_GROUP_SHOW_CHAT, this.handleConnectToGroupShowChat);
    this.messagingService.off(GET_PLAY_TOKEN_FOR_GROUP_SHOW, this.getPlayTokenForGroupShow);
    this.messagingService.off(TICKET_SELLING_STARTED, this.handleTicketSellingStarted);
    this.messagingService.off(UPDATE_GS_SUCCESS, this.handleGsUpdateSuccess);
    this.messagingService.off(TICKET_SELLING_FAILED, this.handleTicketSellingFail);

  }

  @action buyGroupShowTicket = (data, callBackFnc) => {
    GroupShowService.buyGroupShowTicket(data, callBackFnc);
  };

  @action setIsBonusTimeVisible = (isVisible) => {
    this.isBonusTimeVisible = isVisible;
  };

  @action getRemainingTimeUntillGroupShow = (end) => {
    GroupShowService.getExactRemainingTime(end);
  };

  @action getPlayTokenForGroupShow = (data) => {
    ProfileStore.setPsGsLoading(APIStates.PENDING);
    GroupShowService.getPlayTokenForGroupShow(data);
  };

  @action setGroupShowTimer = (groupShowTimer) => {
    this.groupShowTimer = groupShowTimer;
  };

  @action setRemainingTimeGroupShow = (time) => {
    this.remainingTimeLeftUntillGroupShow = time;
  };

  @action closeGroupShowTransitioning = (groupShowTransition) => {
    this.groupShowTransition = groupShowTransition;
  };

  @action
    handleBuyGroupShowTicketResposne = (response) => {
      this.buyGroupShowTicketResponse = response;
      if (FirebaseStore.isStreamingSaas) {
        const user = UserStore.user || {};
        const roomName = ProfileStore.profileName;
        const tokenPayload = { roomname: roomName, username: user.username, accessHash: user.accessHash };
        if (FirebaseStore.newShowsState === NEW_SHOWS_STATE.INSIDE_GS) {
          this.messagingService.emit(GET_PLAY_TOKEN_FOR_GROUP_SHOW, tokenPayload);
        }
      }
    };

  @action setTicketCost = (newCost) => {
    this.ticketCost = newCost;
  };

  @action setTicketGoal = (newGoal) => {
    this.ticketGoal = newGoal;
  };

  @action setShowLength = (newLength) => {
    this.showLength = newLength;
  };

  @action setTimeLimit = (newLimit) => {
    this.timeLimit = newLimit;
  };

  @action setBonusTime = (newLimit) => {
    this.bonusTime = newLimit;
  };

  @action setShowDescription = (newDescription) => {
    this.showDescription = newDescription;
    this.showErrorMsg = false;
  };

  @action startSellingTickets = (data) => {
    if (data.description.length > 0) {
      this.ticketSellingStatus = APIStates.PENDING;
      GroupShowService.startSellingTickets(data);
    } else {
      this.showErrorMsg = true;
    }
  };

  @action handleTicketSellingStarted = () => {
    this.ticketSellingStatus = APIStates.LOADED;
  };

  @action updateGroupShow = (data) => {
    this.ticketSellingStatus = APIStates.PENDING;
    GroupShowService.startSellingTickets(data);
  };

  @action startGroupShow = (data) => {
    this.updateGroupShowStatus = APIStates.PENDING;
    GroupShowService.handleGroupShow(data);
  };

  @action endGroupShow = (data) => {
    this.updateGroupShowStatus = APIStates.PENDING;
    GroupShowService.handleGroupShow(data);
  };

  @action handleGsUpdateSuccess = () => {
    this.updateGroupShowStatus = APIStates.LOADED;
  };

  @action handleTicketSellingFail = () => {
    this.ticketSellingStatus = APIStates.ERROR;
  };

  @action handleRemainingTimeUntillGroupShow = (time) => {
    this.setRemainingTimeGroupShow(time);
    this.setGroupShowTimer(time);
  };

  @action handleConnectToGroupShowChat = (data) => {
    FirebaseRoomService.doFirebaseAuth(
      FirebaseStore.authData.shard,
      data.token,
      data.roomName,
      data.user,
      this.handleGroupShowAuthentication
    );
  };

  handleGroupShowAuthentication = (_, roomName) => {
    ChatStore.watchGroupShowChat(roomName);
  };

  handleDisconnectFromGroupShowChat = (roomName) => {
    const groupShowChatRef = FirebaseService.FirebaseMain.child(`chatRooms/${roomName}/groupShowMessages`);
    this.closeGroupShowTransitioning(true);
    groupShowChatRef?.off?.();
  };

  userHaveTicket = (username) =>
    computed(
      () =>
        FirebaseStore.newShowsDetails !== undefined &&
        FirebaseStore.newShowsDetails !== null &&
        FirebaseStore.newShowsDetails.gsTime &&
        FirebaseStore.newShowsDetails.ticketHolders?.includes?.(username?.toLowerCase?.())
    ).get();

  @computed get numberOfTickets() {
    const { newShowsDetails } = FirebaseStore;

    return newShowsDetails?.ticketHolders?.length || 0;
  }

  @computed get isBroadcasterInGroupShowState() {
    return (
      FirebaseStore.newShowsState === NEW_SHOWS_STATE.GS_SELLING_TICKETS ||
      FirebaseStore.newShowsState === NEW_SHOWS_STATE.INSIDE_GS ||
      FirebaseStore.newShowsState === NEW_SHOWS_STATE.GS_GOAL_REACHED ||
      FirebaseStore.newShowsState === NEW_SHOWS_STATE.BONUS_GS
    );
  }

  @computed get parsedBroadcasterGroupShowTimer() {
    const areHoursVisible = Math.floor(this.remainingTimeLeftUntillGroupShow / HOUR_AS_SECONDS) > 0;
    if (areHoursVisible) {
      return getTimeStringWithHours(this.groupShowTimer);
    }

    return getTimeStringWithoutHours(this.groupShowTimer);
  }

  @computed get broadcasterGroupShowGoalReached() {
    return FirebaseStore.newShowsState === NEW_SHOWS_STATE.GS_GOAL_REACHED;
  }

  @computed get broadcasterInBonusGroupShowState() {
    return FirebaseStore.newShowsState === NEW_SHOWS_STATE.BONUS_GS;
  }

  @computed get broadcasterInsideGroupShow() {
    return (
      FirebaseStore.newShowsState === NEW_SHOWS_STATE.INSIDE_GS ||
      FirebaseStore.newShowsState === NEW_SHOWS_STATE.BONUS_GS ||
      FirebaseStore.newShowsState === NEW_SHOWS_STATE.PAUSED_GS
    );
  }

  @computed get isTicketSellingLoading() {
    return this.ticketSellingStatus === APIStates.PENDING;
  }

  @computed get isGsUpdating() {
    return this.updateGroupShowStatus === APIStates.PENDING;
  }
}

export const GroupShowStoreClass = GroupShowStore;

export default new GroupShowStore(messagingService);
